import type {
  PlayerTag,
  RaidStage,
  RaidStageBuildSnapshot,
  RaidStageDiff,
  RaidStageId,
  RaidStagePlacementDiff,
  RaidPlayerId,
} from '../../types/index.ts'
import { activeStageBuild } from '../../lib/raidStageBuilds'

export interface StageDiffStageView {
  readonly stageId: RaidStageId
  readonly stageName: string
  readonly placement: RaidStagePlacementDiff | null
  readonly hasBuildChange: boolean
  readonly sourceBuild: RaidStageBuildSnapshot | null
  readonly targetBuild: RaidStageBuildSnapshot | null
  readonly addedTags: ReadonlyArray<PlayerTag>
  readonly removedTags: ReadonlyArray<PlayerTag>
}

export interface StageDiffPlayerView {
  readonly playerId: RaidPlayerId
  readonly playerName: string
  readonly stageChanges: ReadonlyArray<StageDiffStageView>
}

export interface StageDiffViewModel {
  readonly players: ReadonlyArray<StageDiffPlayerView>
  readonly playerCount: number
  readonly stageCount: number
}

function compareIds(left: string, right: string): number {
  if (left === right) return 0
  return left < right ? -1 : 1
}

function stageOrder(stages: ReadonlyArray<RaidStage>): ReadonlyMap<RaidStageId, number> {
  const orderedStages: ReadonlyArray<RaidStage> = [...stages].sort((left, right) =>
    left.orderIndex - right.orderIndex || compareIds(left.id, right.id),
  )
  return new Map(orderedStages.map((stage, index) => [stage.id, index]))
}

function crossGroupPlacement(diff: RaidStageDiff): RaidStagePlacementDiff | null {
  const placement = diff.placement
  if (placement === null || placement.beforeGroupId === placement.afterGroupId) return null
  return placement
}

function stageView(diff: RaidStageDiff): StageDiffStageView | null {
  const placement = crossGroupPlacement(diff)
  const hasBuildChange = diff.build !== null
  const hasTagChange = diff.addedTags.length > 0 || diff.removedTags.length > 0
  if (placement === null && !hasBuildChange && !hasTagChange) return null

  return {
    stageId: diff.stageId,
    stageName: diff.stageName,
    placement,
    hasBuildChange,
    sourceBuild: diff.build === null
      ? null
      : activeStageBuild(diff.build.before, diff.build.beforeActiveBuildIndex),
    targetBuild: diff.build === null
      ? null
      : activeStageBuild(diff.build.after, diff.build.afterActiveBuildIndex),
    addedTags: diff.addedTags,
    removedTags: diff.removedTags,
  }
}

/**
 * Turns one GraphQL row per player/stage into the compact player-first model
 * used by the audit card. ALL remains the canonical source represented by the
 * left side of every placement and build change.
 */
export function createStageDiffViewModel(
  diffs: ReadonlyArray<RaidStageDiff>,
  stages: ReadonlyArray<RaidStage>,
  activeStageId: RaidStageId | null,
): StageDiffViewModel {
  const orderByStageId = stageOrder(stages)
  const visibleDiffs: ReadonlyArray<RaidStageDiff> = activeStageId === null
    ? diffs
    : diffs.filter((diff) => diff.stageId === activeStageId)
  let players: ReadonlyArray<StageDiffPlayerView> = []

  for (const diff of visibleDiffs) {
    const change = stageView(diff)
    if (change === null) continue

    const playerIndex = players.findIndex((player) => player.playerId === diff.playerId)
    if (playerIndex < 0) {
      players = [...players, {
        playerId: diff.playerId,
        playerName: diff.playerName,
        stageChanges: [change],
      }]
      continue
    }

    players = players.map((player, index) => index === playerIndex
      ? { ...player, stageChanges: [...player.stageChanges, change] }
      : player)
  }

  players = players.map((player) => ({
    ...player,
    stageChanges: [...player.stageChanges].sort((left, right) => {
      const leftOrder = orderByStageId.get(left.stageId) ?? Number.MAX_SAFE_INTEGER
      const rightOrder = orderByStageId.get(right.stageId) ?? Number.MAX_SAFE_INTEGER
      return leftOrder - rightOrder || compareIds(left.stageId, right.stageId)
    }),
  }))

  const changedStageIds: ReadonlySet<RaidStageId> = new Set(
    players.flatMap((player) => player.stageChanges.map((change) => change.stageId)),
  )

  return {
    players,
    playerCount: players.length,
    stageCount: changedStageIds.size,
  }
}
